import { useEffect, useState } from "react";
import api, { API_BASE } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { TrendingUp, ShoppingBag, Users, Package, FileArchive } from "lucide-react";
import { toast } from "sonner";

const fmtEUR = (v) => (v || 0).toLocaleString("de-DE", { style: "currency", currency: "EUR" });

function Kpi({ label, value, icon: Icon }) {
  return (
    <div className="bg-white border border-[var(--border)] rounded-2xl p-5">
      <div className="flex items-start justify-between">
        <div className="overline">{label}</div>
        <Icon size={18} className="text-[var(--pop-pink)]" />
      </div>
      <div className="mt-3 font-display font-black text-3xl md:text-4xl tracking-tight">{value}</div>
    </div>
  );
}

function BarRow({ label, sub, value, max }) {
  const pct = max > 0 ? (value / max) * 100 : 0;
  return (
    <li className="py-2.5">
      <div className="flex items-baseline justify-between mb-1">
        <div className="text-sm font-medium truncate mr-4">{label}</div>
        <div className="font-mono text-xs font-semibold">{fmtEUR(value)}</div>
      </div>
      <div className="h-2 rounded-full bg-neutral-100 overflow-hidden">
        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: "linear-gradient(90deg,#EB75B5,#d97706)" }} />
      </div>
      {sub && <div className="mt-1 text-xs font-mono text-[var(--text-muted)]">{sub}</div>}
    </li>
  );
}

export default function Revenue() {
  const [days, setDays] = useState("90");
  const [data, setData] = useState(null);
  const [batchOpen, setBatchOpen] = useState(false);
  const now = new Date();
  const [batchMonth, setBatchMonth] = useState(
    `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`
  );
  const [batchBusy, setBatchBusy] = useState(false);

  useEffect(() => {
    api.get("/reports/revenue", { params: { days: Number(days) } })
      .then((r) => setData(r.data))
      .catch(() => setData(null));
  }, [days]);

  const downloadBatch = async () => {
    if (!/^\d{4}-\d{2}$/.test(batchMonth)) {
      toast.error("Bitte Format JJJJ-MM eingeben");
      return;
    }
    setBatchBusy(true);
    try {
      const res = await api.get(`/exports/monthly-invoices.zip`, {
        params: { month: batchMonth }, responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `rechnungslauf-${batchMonth}.zip`; a.click();
      URL.revokeObjectURL(url);
      toast.success(`Rechnungslauf ${batchMonth} heruntergeladen`);
      setBatchOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Download");
    } finally {
      setBatchBusy(false);
    }
  };

  const monthMax = Math.max(...(data?.by_month || []).map((m) => m.revenue), 1);
  const custMax = Math.max(...(data?.by_customer || []).map((c) => c.revenue), 1);
  const itemMax = Math.max(...(data?.by_item || []).map((i) => i.revenue), 1);

  return (
    <div data-testid="page-revenue">
      <PageHeader
        eyebrow="Reporting"
        title="Umsatzbericht"
        description="Aggregierter Umsatz aus versendeten Lieferscheinen (Netto)."
        actions={
          <div className="flex items-center gap-2">
            <Dialog open={batchOpen} onOpenChange={setBatchOpen}>
              <DialogTrigger asChild>
                <Button data-testid="btn-open-batch" variant="outline" className="rounded-full">
                  <FileArchive size={16} className="mr-1" /> Rechnungslauf
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="font-display text-2xl">Monatsrechnungen als ZIP</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <p className="text-sm text-[var(--text-muted)]">
                    Erzeugt zu jedem versendeten Lieferschein des Monats eine Rechnungs-PDF
                    und packt sie mit einer DATEV-Sammelübersicht in ein ZIP.
                  </p>
                  <div>
                    <label className="overline">Monat (JJJJ-MM)</label>
                    <Input
                      data-testid="input-batch-month"
                      value={batchMonth}
                      onChange={(e) => setBatchMonth(e.target.value)}
                      placeholder="2026-08"
                      className="mt-1 rounded-xl"
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button
                    data-testid="btn-download-batch"
                    onClick={downloadBatch}
                    disabled={batchBusy}
                    className="rounded-full bg-[var(--pop-yellow)] text-black hover:bg-[var(--pop-yellow-dark)] font-bold"
                  >
                    {batchBusy ? "Erzeuge…" : "Download ZIP"}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            <Select value={days} onValueChange={setDays}>
              <SelectTrigger className="w-40 rounded-full" data-testid="select-revenue-range"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="30">Letzte 30 Tage</SelectItem>
                <SelectItem value="90">Letzte 90 Tage</SelectItem>
                <SelectItem value="180">Letzte 6 Monate</SelectItem>
                <SelectItem value="365">Letzte 12 Monate</SelectItem>
                <SelectItem value="3650">Alle Zeit</SelectItem>
              </SelectContent>
            </Select>
          </div>
        }
      />

      <section className="grid grid-cols-2 lg:grid-cols-4 gap-4 pop-fade">
        <Kpi label="Netto-Umsatz" value={fmtEUR(data?.total_net)} icon={TrendingUp} />
        <Kpi label="Belege" value={data?.total_orders ?? 0} icon={ShoppingBag} />
        <Kpi label="Ø Belegwert" value={fmtEUR(data?.avg_order_value)} icon={Users} />
        <Kpi label="Verkaufte Einheiten" value={data?.total_units ?? 0} icon={Package} />
      </section>

      <section className="mt-8 grid lg:grid-cols-3 gap-6">
        <div className="bg-white border border-[var(--border)] rounded-2xl p-6" data-testid="revenue-by-month">
          <div className="overline mb-3">Umsatz pro Monat</div>
          <ul className="divide-y divide-[var(--border)]">
            {(data?.by_month || []).map((m) => (
              <BarRow key={m.month} label={m.month} value={m.revenue} max={monthMax} />
            ))}
            {(!data?.by_month || data.by_month.length === 0) && (
              <li className="py-8 text-sm text-[var(--text-muted)] text-center">Noch keine Daten.</li>
            )}
          </ul>
        </div>

        <div className="bg-white border border-[var(--border)] rounded-2xl p-6" data-testid="revenue-by-customer">
          <div className="overline mb-3">Top Kunden</div>
          <ul className="divide-y divide-[var(--border)]">
            {(data?.by_customer || []).map((c) => (
              <BarRow key={c.customer_id} label={c.name} sub={`${c.orders} Belege`} value={c.revenue} max={custMax} />
            ))}
            {(!data?.by_customer || data.by_customer.length === 0) && (
              <li className="py-8 text-sm text-[var(--text-muted)] text-center">Noch keine Daten.</li>
            )}
          </ul>
        </div>

        <div className="bg-white border border-[var(--border)] rounded-2xl p-6" data-testid="revenue-by-item">
          <div className="overline mb-3">Top Artikel</div>
          <ul className="divide-y divide-[var(--border)]">
            {(data?.by_item || []).map((it) => (
              <BarRow key={it.item_id} label={it.name} sub={`${it.units} Einheiten · ${it.sku}`} value={it.revenue} max={itemMax} />
            ))}
            {(!data?.by_item || data.by_item.length === 0) && (
              <li className="py-8 text-sm text-[var(--text-muted)] text-center">Noch keine Daten.</li>
            )}
          </ul>
        </div>
      </section>
    </div>
  );
}
